/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  COMMAND_PRIORITY_LOW,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_UP_COMMAND,
  KEY_ENTER_COMMAND,
  KEY_TAB_COMMAND,
} from 'lexical';
import { useEffect, useRef } from 'react';
import type { Key } from 'react-aria-components';
import type { useListState } from 'react-stately';

interface ListBoxNavigationPluginProps {
  activeTrigger: string | null;
  triggerQuery: string;
  listState: ReturnType<typeof useListState>;
}

function getListBoxItemElements(listBoxEl: HTMLElement): HTMLElement[] {
  return Array.from(listBoxEl.querySelectorAll<HTMLElement>('[data-key]:not([aria-disabled="true"])'));
}

function getNextKey(listBoxEl: HTMLElement, currentKey: Key | null): Key | null {
  const items = getListBoxItemElements(listBoxEl);
  if (items.length === 0) return null;
  if (currentKey === null) return items[0].getAttribute('data-key');
  const idx = items.findIndex((el) => el.getAttribute('data-key') === String(currentKey));
  return items[idx === -1 || idx === items.length - 1 ? 0 : idx + 1].getAttribute('data-key');
}

function getPrevKey(listBoxEl: HTMLElement, currentKey: Key | null): Key | null {
  const items = getListBoxItemElements(listBoxEl);
  if (items.length === 0) return null;
  if (currentKey === null) return items[items.length - 1].getAttribute('data-key');
  const idx = items.findIndex((el) => el.getAttribute('data-key') === String(currentKey));
  return items[idx <= 0 ? items.length - 1 : idx - 1].getAttribute('data-key');
}

/**
 * Handles keyboard navigation between the Lexical editor and a consumer-provided `<ListBox>`.
 *
 * Uses RAC's virtual-focus strategy — physical DOM focus stays in the Lexical editor.
 * Virtual focus is driven by calling `selectionManager.setFocusedKey` on the RAC `ListState`.
 *
 * Key bindings:
 * - **ArrowDown** — move virtual focus to the next item (wraps to first).
 * - **ArrowUp** — move virtual focus to the previous item (wraps to last).
 * - **Enter** — commit the virtually focused item via `.click()`.
 * - **Tab** — same as Enter when an item is focused; otherwise passes through.
 */
function ListBoxNavigationPlugin({ activeTrigger, triggerQuery, listState }: ListBoxNavigationPluginProps) {
  const [editor] = useLexicalComposerContext();
  const focusedKeyRef = useRef<Key | null>(null);

  useEffect(() => {
    if (activeTrigger === null) {
      listState?.selectionManager.setFocused(false);
      listState?.selectionManager.setFocusedKey(null);
      focusedKeyRef.current = null;
    }
  }, [activeTrigger, listState]);

  // Focus first item when trigger opens or filtered collection invalidates key.
  // Preserves arrow-key selection across keystrokes when the focused key still exists.
  useEffect(() => {
    if (activeTrigger === null || listState === null) return;
    const currentKey = focusedKeyRef.current;
    const keyStillExists = currentKey !== null && listState.collection.getItem(currentKey) !== null;
    if (keyStillExists) return;
    const firstKey = listState.collection.getFirstKey();
    focusedKeyRef.current = firstKey;
    listState.selectionManager.setFocused(true);
    listState.selectionManager.setFocusedKey(firstKey);
  }, [activeTrigger, triggerQuery, listState]);

  useEffect(() => {
    return editor.registerCommand(
      KEY_ARROW_DOWN_COMMAND,
      (event) => {
        if (activeTrigger === null || !listState) return false;
        const listBoxEl = document.querySelector<HTMLElement>('[role="listbox"]');
        if (!listBoxEl) return false;
        event.preventDefault();
        const next = getNextKey(listBoxEl, focusedKeyRef.current);
        focusedKeyRef.current = next;
        listState.selectionManager.setFocused(true);
        listState.selectionManager.setFocusedKey(next);
        return true;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor, activeTrigger, listState]);

  useEffect(() => {
    return editor.registerCommand(
      KEY_ARROW_UP_COMMAND,
      (event) => {
        if (activeTrigger === null || !listState) return false;
        const listBoxEl = document.querySelector<HTMLElement>('[role="listbox"]');
        if (!listBoxEl) return false;
        event.preventDefault();
        const prev = getPrevKey(listBoxEl, focusedKeyRef.current);
        focusedKeyRef.current = prev;
        listState.selectionManager.setFocused(true);
        listState.selectionManager.setFocusedKey(prev);
        return true;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor, activeTrigger, listState]);

  // RAC's usePress detects element.click() as a virtual click (e.detail === 0) and fires onAction.
  useEffect(() => {
    return editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event) => {
        if (activeTrigger === null || focusedKeyRef.current === null || !listState) return false;
        const listBoxEl = document.querySelector<HTMLElement>('[role="listbox"]');
        if (!listBoxEl) return false;
        event?.preventDefault();
        const el = listBoxEl.querySelector<HTMLElement>(`[data-key="${CSS.escape(String(focusedKeyRef.current))}"]`);
        el?.click();
        return true;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor, activeTrigger, listState]);

  useEffect(() => {
    return editor.registerCommand(
      KEY_TAB_COMMAND,
      (event) => {
        if (activeTrigger === null || focusedKeyRef.current === null || !listState) return false;
        const listBoxEl = document.querySelector<HTMLElement>('[role="listbox"]');
        if (!listBoxEl) return false;
        event.preventDefault();
        const el = listBoxEl.querySelector<HTMLElement>(`[data-key="${CSS.escape(String(focusedKeyRef.current))}"]`);
        el?.click();
        return true;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor, activeTrigger, listState]);

  return null;
}

export { ListBoxNavigationPlugin };
